import * as actionTypes from './OfflineBookingActionTypes';

const initialState = {
  bookings: [],
  spaces: [],
  spacesPage: 1,
  spacesTotalPages: 0,
  spacesTotalElements: 0,
  searchQuery: '',
  spaceDetails: null,
  spaceBookings: [],
  calendarData: {}, // Calendar data from API in format: { "DD-MM-YYYY": [bookings] }
  clientSuggestions: [],
  loadingClients: false,
  paymentData: null, // Current payment being viewed/edited
  payments: {}, // Map of bookingId -> payment data for caching
  availability: [], // Booking availability data
  loadingAvailability: false,
  availabilityError: null,
  loading: false,
  loadingSpaces: false,
  loadingSpaceDetails: false,
  loadingBookings: false,
  loadingPayment: false,
  error: null,
  success: false
};

const offlineBookingReducer = (state = initialState, action) => {
  switch (action.type) {
    // Fetch All Offline Bookings
    case actionTypes.FETCH_OFFLINE_BOOKINGS:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case actionTypes.FETCH_OFFLINE_BOOKINGS_SUCCESS:
      return {
        ...state,
        loading: false,
        bookings: action.payload,
        error: null
      };
    
    case actionTypes.FETCH_OFFLINE_BOOKINGS_FAIL:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Create Offline Booking
    case actionTypes.CREATE_OFFLINE_BOOKING:
      return {
        ...state,
        loading: true,
        error: null,
        success: false
      };
    
    case actionTypes.CREATE_OFFLINE_BOOKING_SUCCESS:
      return {
        ...state,
        loading: false,
        bookings: [action.payload, ...state.bookings],
        error: null,
        success: true
      };
    
    case actionTypes.CREATE_OFFLINE_BOOKING_FAIL:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: false
      };

    // Update Offline Booking
    case actionTypes.UPDATE_OFFLINE_BOOKING:
      return {
        ...state,
        loading: true,
        error: null,
        success: false
      };
    
    case actionTypes.UPDATE_OFFLINE_BOOKING_SUCCESS:
      return {
        ...state,
        loading: false,
        bookings: state.bookings.map(booking =>
          booking.id === action.payload.id ? action.payload : booking
        ),
        error: null,
        success: true
      };
    
    case actionTypes.UPDATE_OFFLINE_BOOKING_FAIL:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: false
      };

    // Delete Offline Booking
    case actionTypes.DELETE_OFFLINE_BOOKING:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case actionTypes.DELETE_OFFLINE_BOOKING_SUCCESS:
      return {
        ...state,
        loading: false,
        bookings: state.bookings.filter(booking => booking.id !== action.payload),
        error: null
      };
    
    case actionTypes.DELETE_OFFLINE_BOOKING_FAIL:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Fetch Spaces for Booking
    case actionTypes.FETCH_SPACES_FOR_BOOKING:
      return {
        ...state,
        loadingSpaces: true,
        error: null
      };
    
    case actionTypes.FETCH_SPACES_FOR_BOOKING_SUCCESS:
      // Support for pagination: append or replace results based on meta.appendResults
      const shouldAppend = action.meta?.appendResults === true;
      const newSpaces = shouldAppend 
        ? [...state.spaces, ...action.payload]
        : action.payload;
      
      return {
        ...state,
        loadingSpaces: false,
        spaces: newSpaces,
        spacesPage: action.meta?.page || 1,
        spacesTotalPages: action.meta?.totalPages || 0,
        spacesTotalElements: action.meta?.totalElements || newSpaces.length,
        error: null
      };
    
    case actionTypes.FETCH_SPACES_FOR_BOOKING_FAIL:
      return {
        ...state,
        loadingSpaces: false,
        error: action.payload
      };

    // Search Spaces for Booking
    case actionTypes.SEARCH_SPACES_FOR_BOOKING:
      return {
        ...state,
        loadingSpaces: true,
        error: null
      };
    
    case actionTypes.SEARCH_SPACES_FOR_BOOKING_SUCCESS:
      return {
        ...state,
        loadingSpaces: false,
        spaces: action.payload,
        spacesPage: action.meta?.page || 1,
        spacesTotalPages: action.meta?.totalPages || 0,
        spacesTotalElements: action.meta?.totalElements || action.payload.length,
        searchQuery: action.meta?.searchQuery || '',
        error: null
      };
    
    case actionTypes.SEARCH_SPACES_FOR_BOOKING_FAIL:
      return {
        ...state,
        loadingSpaces: false,
        error: action.payload
      };

    // Fetch Space By ID
    case actionTypes.FETCH_SPACE_BY_ID:
      return {
        ...state,
        loadingSpaceDetails: true,
        error: null
      };
    
    case actionTypes.FETCH_SPACE_BY_ID_SUCCESS:
      return {
        ...state,
        loadingSpaceDetails: false,
        spaceDetails: action.payload,
        error: null
      };
    
    case actionTypes.FETCH_SPACE_BY_ID_FAIL:
      return {
        ...state,
        loadingSpaceDetails: false,
        spaceDetails: null,
        error: action.payload
      };

    // Fetch Space Bookings
    case actionTypes.FETCH_SPACE_BOOKINGS:
      return {
        ...state,
        loadingBookings: true,
        error: null
      };
    
    case actionTypes.FETCH_SPACE_BOOKINGS_SUCCESS:
      return {
        ...state,
        loadingBookings: false,
        spaceBookings: action.payload,
        error: null
      };
    
    case actionTypes.FETCH_SPACE_BOOKINGS_FAIL:
      return {
        ...state,
        loadingBookings: false,
        error: action.payload
      };

    // Clear Error
    case actionTypes.CLEAR_OFFLINE_BOOKING_ERROR:
      return {
        ...state,
        error: null,
        success: false
      };

    // Reset Form
    case actionTypes.RESET_OFFLINE_BOOKING_FORM:
      return {
        ...state,
        error: null,
        success: false,
        spaceDetails: null
      };

    // Clear Space Details (when changing space selection)
    case actionTypes.CLEAR_SPACE_DETAILS:
      return {
        ...state,
        spaceDetails: null,
        spaceBookings: []
      };

    // Create Offline Payment
    case actionTypes.CREATE_OFFLINE_PAYMENT:
      return {
        ...state,
        loadingPayment: true,
        error: null
      };
    
    case actionTypes.CREATE_OFFLINE_PAYMENT_SUCCESS:
      const newPayment = action.payload;
      
      // Add safety check for undefined payload
      if (!newPayment) {
        return {
          ...state,
          loadingPayment: false,
          error: 'Invalid payment data received',
          success: false
        };
      }
      
      return {
        ...state,
        loadingPayment: false,
        paymentData: newPayment,
        payments: {
          ...state.payments,
          [newPayment.bookingId || newPayment.id]: newPayment
        },
        // Update booking payment status and booking status if payment is COMPLETED
        bookings: state.bookings.map(booking => {
          const isMatchingBooking = booking.id === newPayment.bookingId || booking.bookingId === newPayment.bookingId;
          if (isMatchingBooking) {
            return {
              ...booking,
              paymentStatus: newPayment.paymentStatus,
              // Update booking status to CONFIRMED if payment is COMPLETED
              // Scheduler will auto-change CONFIRMED -> COMPLETED after booking ends
              bookingStatus: newPayment.paymentStatus === 'COMPLETED' ? 'CONFIRMED' : booking.bookingStatus
            };
          }
          return booking;
        }),
        error: null,
        success: true
      };
    
    case actionTypes.CREATE_OFFLINE_PAYMENT_FAIL:
      return {
        ...state,
        loadingPayment: false,
        error: action.payload,
        success: false
      };

    // Update Offline Payment
    case actionTypes.UPDATE_OFFLINE_PAYMENT:
      return {
        ...state,
        loadingPayment: true,
        error: null
      };
    
    case actionTypes.UPDATE_OFFLINE_PAYMENT_SUCCESS:
      const updatedPayment = action.payload;
      
      // Add safety check for undefined payload
      if (!updatedPayment) {
        return {
          ...state,
          loadingPayment: false,
          error: 'Invalid payment data received',
          success: false
        };
      }
      
      return {
        ...state,
        loadingPayment: false,
        paymentData: updatedPayment,
        payments: {
          ...state.payments,
          [updatedPayment.bookingId || updatedPayment.id]: updatedPayment
        },
        // Update booking payment status and booking status if payment is COMPLETED
        bookings: state.bookings.map(booking => {
          const isMatchingBooking = booking.id === updatedPayment.bookingId || booking.bookingId === updatedPayment.bookingId;
          if (isMatchingBooking) {
            return {
              ...booking,
              paymentStatus: updatedPayment.paymentStatus,
              // Update booking status to CONFIRMED if payment is COMPLETED
              // Scheduler will auto-change CONFIRMED -> COMPLETED after booking ends
              bookingStatus: updatedPayment.paymentStatus === 'COMPLETED' ? 'CONFIRMED' : booking.bookingStatus
            };
          }
          return booking;
        }),
        error: null,
        success: true
      };
    
    case actionTypes.UPDATE_OFFLINE_PAYMENT_FAIL:
      return {
        ...state,
        loadingPayment: false,
        error: action.payload,
        success: false
      };

    // Fetch Offline Payment
    case actionTypes.FETCH_OFFLINE_PAYMENT:
      return {
        ...state,
        loadingPayment: true,
        error: null
      };
    
    case actionTypes.FETCH_OFFLINE_PAYMENT_SUCCESS:
      const fetchedPayment = action.payload;
      return {
        ...state,
        loadingPayment: false,
        paymentData: fetchedPayment,
        payments: {
          ...state.payments,
          [fetchedPayment.bookingId]: fetchedPayment
        },
        error: null
      };
    
    case actionTypes.FETCH_OFFLINE_PAYMENT_FAIL:
      return {
        ...state,
        loadingPayment: false,
        paymentData: null,
        error: action.payload
      };

    // Fetch Calendar Data (all booking types for a space)
    case actionTypes.FETCH_CALENDAR_DATA:
      return {
        ...state,
        loadingBookings: true,
        error: null
      };
    
    case actionTypes.FETCH_CALENDAR_DATA_SUCCESS:
      return {
        ...state,
        loadingBookings: false,
        calendarData: action.payload?.bookings || action.payload || {},
        error: null
      };
    
    case actionTypes.FETCH_CALENDAR_DATA_FAIL:
      return {
        ...state,
        loadingBookings: false,
        error: action.payload
      };

    // Search clients (existing users)
    case actionTypes.SEARCH_CLIENTS:
      return {
        ...state,
        loadingClients: true,
        error: null
      };
    case actionTypes.SEARCH_CLIENTS_SUCCESS:
      return {
        ...state,
        loadingClients: false,
        clientSuggestions: Array.isArray(action.payload) ? action.payload : [],
        error: null
      };
    case actionTypes.SEARCH_CLIENTS_FAIL:
      return {
        ...state,
        loadingClients: false,
        clientSuggestions: [],
        error: action.payload
      };
    case actionTypes.CLEAR_CLIENT_SUGGESTIONS:
      return {
        ...state,
        clientSuggestions: []
      };

    // Fetch Booking Availability
    case actionTypes.FETCH_BOOKING_AVAILABILITY:
      return {
        ...state,
        loadingAvailability: true,
        availabilityError: null
      };
    
    case actionTypes.FETCH_BOOKING_AVAILABILITY_SUCCESS:
      return {
        ...state,
        loadingAvailability: false,
        availability: action.payload,
        availabilityError: null
      };
    
    case actionTypes.FETCH_BOOKING_AVAILABILITY_FAIL:
      return {
        ...state,
        loadingAvailability: false,
        availabilityError: action.payload
      };
    
    case actionTypes.CLEAR_BOOKING_AVAILABILITY:
      return {
        ...state,
        availability: [],
        availabilityError: null
      };

    // Confirm Offline Booking
    case actionTypes.CONFIRM_OFFLINE_BOOKING:
      return {
        ...state,
        loading: true,
        error: null,
        success: false
      };
    
    case actionTypes.CONFIRM_OFFLINE_BOOKING_SUCCESS:
      return {
        ...state,
        loading: false,
        bookings: state.bookings.map(booking =>
          (booking.id === action.payload.id || booking._id === action.payload.id) 
            ? { ...booking, bookingStatus: 'CONFIRMED' } 
            : booking
        ),
        error: null,
        success: true
      };
    
    case actionTypes.CONFIRM_OFFLINE_BOOKING_FAIL:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: false
      };

    // Cancel Offline Booking
    case actionTypes.CANCEL_OFFLINE_BOOKING:
      return {
        ...state,
        loading: true,
        error: null,
        success: false
      };
    
    case actionTypes.CANCEL_OFFLINE_BOOKING_SUCCESS:
      return {
        ...state,
        loading: false,
        bookings: state.bookings.map(booking =>
          (booking.id === action.payload.id || booking._id === action.payload.id) 
            ? { ...booking, bookingStatus: 'CANCELLED' } 
            : booking
        ),
        error: null,
        success: true
      };
    
    case actionTypes.CANCEL_OFFLINE_BOOKING_FAIL:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: false
      };

    default:
      return state;
  }
};

export default offlineBookingReducer;